import React, { useState, useCallback, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Play,
  Loader2,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  Copy,
  Clock,
} from 'lucide-react';
import { api } from '../../../lib/api';

interface HttpConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
  onBatchChange?: (updates: Record<string, any>) => void;
}

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-green-100 text-green-700 border-green-300',
  POST: 'bg-blue-100 text-blue-700 border-blue-300',
  PUT: 'bg-amber-100 text-amber-700 border-amber-300',
  PATCH: 'bg-purple-100 text-purple-700 border-purple-300',
  DELETE: 'bg-red-100 text-red-700 border-red-300',
};

const CONTENT_TYPES = [
  { value: 'application/json', label: 'JSON' },
  { value: 'application/x-www-form-urlencoded', label: 'Form URL Encoded' },
  { value: 'text/plain', label: 'Text Plain' },
  { value: 'application/xml', label: 'XML' },
  { value: 'multipart/form-data', label: 'Multipart Form Data' },
];

const AUTH_TYPES = [
  { value: 'none', label: 'Nenhuma' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'basic', label: 'Basic Auth' },
  { value: 'api-key', label: 'API Key' },
];

/**
 * Infers variable paths from a tested HTTP response body.
 * The runtime stores the response as `{{var}} = { data, status, headers }`, so every
 * field path is prefixed with "data." (e.g. {Nome, Telefone} -> ["data.Nome", "data.Telefone"]).
 * Walks up to 2 levels deep; arrays are exposed as a single path.
 */
function extractResponseFields(rawData: any): string[] {
  let root = rawData;
  if (typeof root === 'string') {
    try {
      root = JSON.parse(root);
    } catch {
      return [];
    }
  }
  if (!root || typeof root !== 'object') {
    return [];
  }

  const fields: string[] = [];
  const walk = (node: any, prefix: string, depth: number) => {
    if (Array.isArray(node)) {
      fields.push(prefix);
      return;
    }
    if (node && typeof node === 'object') {
      for (const key of Object.keys(node)) {
        const path = prefix ? `${prefix}.${key}` : key;
        const value = node[key];
        if (value && typeof value === 'object' && !Array.isArray(value) && depth < 2) {
          walk(value, path, depth + 1);
        } else {
          fields.push(path);
        }
      }
    }
  };
  walk(root, 'data', 0);
  return Array.from(new Set(fields)).slice(0, 50);
}

export const HttpConfig: React.FC<HttpConfigProps> = ({ config, onChange, onBatchChange }) => {
  const [activeTab, setActiveTab] = useState<'headers' | 'body' | 'auth'>('headers');
  const [testResult, setTestResult] = useState<any>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [showTestResult, setShowTestResult] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // ===== HEADERS MANAGEMENT =====
  const headers: Array<{ key: string; value: string }> = config.headersList || [];

  // Sync the headers object from headersList whenever it changes
  useEffect(() => {
    const obj: Record<string, string> = {};
    headers.forEach((h) => {
      if (h.key.trim()) {
        obj[h.key.trim()] = h.value;
      }
    });
    // Only sync if headers object actually differs
    const currentHeaders = config.headers || {};
    const isSame = Object.keys(obj).length === Object.keys(currentHeaders).length
      && Object.entries(obj).every(([k, v]) => currentHeaders[k] === v);
    if (!isSame) {
      onChange('headers', obj);
    }
  }, [config.headersList]);

  const addHeader = useCallback(() => {
    const newHeaders = [...headers, { key: '', value: '' }];
    onChange('headersList', newHeaders);
  }, [headers, onChange]);

  const updateHeader = useCallback(
    (index: number, field: 'key' | 'value', val: string) => {
      const newHeaders = headers.map((h, i) =>
        i === index ? { ...h, [field]: val } : h,
      );
      onChange('headersList', newHeaders);
    },
    [headers, onChange],
  );

  const removeHeader = useCallback(
    (index: number) => {
      const newHeaders = headers.filter((_, i) => i !== index);
      onChange('headersList', newHeaders);
    },
    [headers, onChange],
  );

  // ===== AUTH =====
  const authType = config.authType || 'none';

  const applyAuth = useCallback(
    (type: string, token?: string, username?: string, password?: string, apiKeyHeader?: string, apiKeyValue?: string) => {
      if (onBatchChange) {
        onBatchChange({
          authType: type,
          authToken: token || '',
          authUsername: username || '',
          authPassword: password || '',
          authApiKeyHeader: apiKeyHeader || '',
          authApiKeyValue: apiKeyValue || '',
        });
      } else {
        onChange('authType', type);
      }
    },
    [onChange, onBatchChange],
  );

  // Build effective headers (merge auth with manual headers)
  const buildEffectiveHeaders = useCallback((): Record<string, string> => {
    const h: Record<string, string> = { ...(config.headers || {}) };

    // Apply content type
    if (config.contentType && !h['Content-Type']) {
      h['Content-Type'] = config.contentType;
    }

    // Apply auth
    if (authType === 'bearer' && config.authToken) {
      h['Authorization'] = `Bearer ${config.authToken}`;
    } else if (authType === 'basic' && config.authUsername) {
      const encoded = btoa(`${config.authUsername}:${config.authPassword || ''}`);
      h['Authorization'] = `Basic ${encoded}`;
    } else if (authType === 'api-key' && config.authApiKeyHeader && config.authApiKeyValue) {
      h[config.authApiKeyHeader] = config.authApiKeyValue;
    }

    return h;
  }, [config, authType]);

  // ===== TEST =====
  const handleTest = useCallback(async () => {
    if (!config.url) return;
    setIsTesting(true);
    setTestResult(null);
    setShowTestResult(true);

    try {
      const effectiveHeaders = buildEffectiveHeaders();

      const result = await (api as any).request('/flows/test-http', {
        method: 'POST',
        body: JSON.stringify({
          url: config.url,
          method: config.method || 'GET',
          headers: effectiveHeaders,
          body: config.body || undefined,
          timeout: config.timeout || 30,
          contentType: config.contentType,
        }),
      });
      setTestResult(result);

      // Auto-detect response fields so the variable picker can suggest them directly
      // (e.g. {{resposta.data.Nome}}) instead of a single generic "data".
      if (result?.success && result.data !== undefined) {
        const detected = extractResponseFields(result.data);
        if (detected.length > 0) {
          onChange('responseFields', [...detected, 'status']);
        }
      }
    } catch (error: any) {
      setTestResult({
        success: false,
        error: error.message || 'Erro ao testar requisição',
        duration: 0,
      });
    } finally {
      setIsTesting(false);
    }
  }, [config, buildEffectiveHeaders, onChange]);

  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
  }, []);

  const method = config.method || 'GET';
  const methodColor = METHOD_COLORS[method] || METHOD_COLORS.GET;
  const hasBody = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

  return (
    <div className="space-y-4">
      {/* Explanation */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 leading-relaxed">
        <strong>Como funciona:</strong> Faz uma requisição HTTP para uma API
        externa. Configure método, URL, headers, corpo e autenticação. A
        resposta completa (status, dados, headers) é salva na variável
        configurada. Suporta variáveis em todos os campos usando{' '}
        <code className="bg-blue-100 px-1 rounded">{'{{variavel}}'}</code>.
      </div>

      {/* Method + URL */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Requisição
        </label>
        <div className="flex gap-2">
          <select
            className={`px-2 py-2 border rounded-lg text-sm font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 ${methodColor}`}
            value={method}
            onChange={(e) => onChange('method', e.target.value)}
          >
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
            <option value="DELETE">DELETE</option>
          </select>
          <input
            type="text"
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
            placeholder="https://api.exemplo.com/endpoint"
            value={config.url || ''}
            onChange={(e) => onChange('url', e.target.value)}
          />
        </div>
      </div>

      {/* Tabs: Headers | Body | Auth */}
      <div>
        <div className="flex border-b border-slate-200">
          <button
            type="button"
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'headers'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('headers')}
          >
            Headers
            {headers.length > 0 && (
              <span className="ml-1 bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full text-[10px]">
                {headers.length}
              </span>
            )}
          </button>
          <button
            type="button"
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'body'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('body')}
          >
            Body
            {config.body && (
              <span className="ml-1 w-2 h-2 bg-blue-500 rounded-full inline-block" />
            )}
          </button>
          <button
            type="button"
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'auth'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('auth')}
          >
            Auth
            {authType !== 'none' && (
              <span className="ml-1 w-2 h-2 bg-green-500 rounded-full inline-block" />
            )}
          </button>
        </div>

        <div className="pt-3">
          {/* HEADERS TAB */}
          {activeTab === 'headers' && (
            <div className="space-y-2">
              {headers.map((header, index) => (
                <div key={index} className="flex gap-1 items-start">
                  <input
                    type="text"
                    className="flex-1 px-2 py-1.5 border border-slate-300 rounded text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Header-Name"
                    value={header.key}
                    onChange={(e) => updateHeader(index, 'key', e.target.value)}
                  />
                  <input
                    type="text"
                    className="flex-1 px-2 py-1.5 border border-slate-300 rounded text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Valor"
                    value={header.value}
                    onChange={(e) =>
                      updateHeader(index, 'value', e.target.value)
                    }
                  />
                  <button
                    type="button"
                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                    onClick={() => removeHeader(index)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 py-1"
                onClick={addHeader}
              >
                <Plus className="w-3 h-3" /> Adicionar header
              </button>

              {/* Content Type shortcut */}
              <div className="mt-2 pt-2 border-t border-slate-100">
                <label className="block text-[11px] font-medium text-slate-600 mb-1">
                  Content-Type
                </label>
                <select
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={config.contentType || 'application/json'}
                  onChange={(e) => onChange('contentType', e.target.value)}
                >
                  {CONTENT_TYPES.map((ct) => (
                    <option key={ct.value} value={ct.value}>
                      {ct.label} ({ct.value})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* BODY TAB */}
          {activeTab === 'body' && (
            <div className="space-y-2">
              {!hasBody && (
                <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
                  O método <strong>{method}</strong> normalmente não envia body.
                  Mude para POST, PUT ou PATCH se precisar enviar dados.
                </div>
              )}
              <textarea
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50"
                rows={8}
                placeholder={
                  config.contentType === 'application/json'
                    ? '{\n  "chave": "valor",\n  "nome": "{{sender_name}}"\n}'
                    : 'campo1=valor1&campo2={{variavel}}'
                }
                value={config.body || ''}
                onChange={(e) => onChange('body', e.target.value)}
              />
              {config.body && config.contentType === 'application/json' && (
                <JsonValidator json={config.body} />
              )}
            </div>
          )}

          {/* AUTH TAB */}
          {activeTab === 'auth' && (
            <div className="space-y-3">
              <select
                className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={authType}
                onChange={(e) =>
                  applyAuth(e.target.value)
                }
              >
                {AUTH_TYPES.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>

              {authType === 'bearer' && (
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">
                    Token
                  </label>
                  <input
                    type="password"
                    className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="eyJhbGciOiJIUzI1NiIs..."
                    value={config.authToken || ''}
                    onChange={(e) => onChange('authToken', e.target.value)}
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    Será enviado como: Authorization: Bearer {'<token>'}
                  </p>
                </div>
              )}

              {authType === 'basic' && (
                <div className="space-y-2">
                  <div>
                    <label className="block text-[11px] font-medium text-slate-600 mb-1">
                      Usuário
                    </label>
                    <input
                      type="text"
                      className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="username"
                      value={config.authUsername || ''}
                      onChange={(e) => onChange('authUsername', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-slate-600 mb-1">
                      Senha
                    </label>
                    <input
                      type="password"
                      className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="password"
                      value={config.authPassword || ''}
                      onChange={(e) => onChange('authPassword', e.target.value)}
                    />
                  </div>
                </div>
              )}

              {authType === 'api-key' && (
                <div className="space-y-2">
                  <div>
                    <label className="block text-[11px] font-medium text-slate-600 mb-1">
                      Nome do Header
                    </label>
                    <input
                      type="text"
                      className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="X-API-Key"
                      value={config.authApiKeyHeader || ''}
                      onChange={(e) =>
                        onChange('authApiKeyHeader', e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-slate-600 mb-1">
                      Valor da API Key
                    </label>
                    <input
                      type="password"
                      className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="sua-api-key-aqui"
                      value={config.authApiKeyValue || ''}
                      onChange={(e) =>
                        onChange('authApiKeyValue', e.target.value)
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Response Variable */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Salvar resposta na variável
        </label>
        <input
          type="text"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Ex: api_response"
          value={config.responseVariable || ''}
          onChange={(e) => onChange('responseVariable', e.target.value.replace(/\s/g, '_'))}
        />
        {config.responseVariable && (
          <div className="mt-1 text-[10px] text-slate-500 space-y-0.5">
            <p>
              Acesse com:{' '}
              <code className="bg-slate-100 px-1 rounded">
                {`{{${config.responseVariable}.data}}`}
              </code>
            </p>
            <p>
              Status:{' '}
              <code className="bg-slate-100 px-1 rounded">
                {`{{${config.responseVariable}.status}}`}
              </code>
            </p>
          </div>
        )}
        {config.responseVariable && Array.isArray(config.responseFields) && config.responseFields.length > 0 && (
          <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2">
            <p className="text-[11px] font-medium text-emerald-700 mb-1">
              {config.responseFields.filter((f: string) => f !== 'status').length} campo(s) detectado(s) — disponíveis no seletor de variáveis:
            </p>
            <div className="flex flex-wrap gap-1">
              {config.responseFields
                .filter((f: string) => f !== 'status')
                .slice(0, 24)
                .map((f: string) => (
                  <code key={f} className="bg-white border border-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded text-[10px]">
                    {`{{${config.responseVariable}.${f}}}`}
                  </code>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Advanced Settings */}
      <div>
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-slate-600 hover:text-slate-800"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronRight className="w-3 h-3" />
          )}
          Configurações avançadas
        </button>

        {showAdvanced && (
          <div className="mt-2 space-y-3 pl-2 border-l-2 border-slate-200">
            <div>
              <label className="block text-[11px] font-medium text-slate-600 mb-1">
                Timeout (segundos)
              </label>
              <input
                type="number"
                className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="30"
                min={1}
                max={120}
                value={config.timeout || 30}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 30;
                  onChange('timeout', Math.min(120, Math.max(1, val)));
                }}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="followRedirects"
                className="rounded border-slate-300"
                checked={config.followRedirects !== false}
                onChange={(e) =>
                  onChange('followRedirects', e.target.checked)
                }
              />
              <label htmlFor="followRedirects" className="text-xs text-slate-600">
                Seguir redirecionamentos
              </label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="continueOnError"
                className="rounded border-slate-300"
                checked={config.continueOnError !== false}
                onChange={(e) =>
                  onChange('continueOnError', e.target.checked)
                }
              />
              <label htmlFor="continueOnError" className="text-xs text-slate-600">
                Continuar fluxo mesmo com erro HTTP
              </label>
            </div>
          </div>
        )}
      </div>

      {/* TEST BUTTON */}
      <div className="border-t border-slate-200 pt-3">
        <button
          type="button"
          disabled={!config.url || isTesting}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          onClick={handleTest}
        >
          {isTesting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Testando...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Testar Requisição
            </>
          )}
        </button>
      </div>

      {/* TEST RESULT */}
      {showTestResult && testResult && (
        <TestResultPanel
          result={testResult}
          onClose={() => setShowTestResult(false)}
          onCopy={copyToClipboard}
        />
      )}
    </div>
  );
};

// ===== JSON Validator Component =====
const JsonValidator: React.FC<{ json: string }> = ({ json }) => {
  let isValid = true;
  let errorMsg = '';

  try {
    JSON.parse(json);
  } catch (e: any) {
    isValid = false;
    errorMsg = e.message;
  }

  if (isValid) {
    return (
      <div className="flex items-center gap-1 text-[10px] text-green-600">
        <CheckCircle className="w-3 h-3" /> JSON válido
      </div>
    );
  }

  return (
    <div className="flex items-start gap-1 text-[10px] text-red-600">
      <XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
      <span>JSON inválido: {errorMsg}</span>
    </div>
  );
};

// ===== Test Result Panel =====
const TestResultPanel: React.FC<{
  result: any;
  onClose: () => void;
  onCopy: (text: string) => void;
}> = ({ result, onClose, onCopy }) => {
  const [activeResultTab, setActiveResultTab] = useState<
    'body' | 'headers' | 'info'
  >('body');

  const isSuccess = result.success && result.status >= 200 && result.status < 300;
  const isError = !result.success;
  const statusColor = isError
    ? 'bg-red-50 border-red-200'
    : isSuccess
      ? 'bg-green-50 border-green-200'
      : 'bg-amber-50 border-amber-200';
  const statusTextColor = isError
    ? 'text-red-700'
    : isSuccess
      ? 'text-green-700'
      : 'text-amber-700';

  const responseBody =
    typeof result.data === 'string'
      ? result.data
      : JSON.stringify(result.data, null, 2);

  return (
    <div className={`border rounded-lg overflow-hidden ${statusColor}`}>
      {/* Status Bar */}
      <div
        className={`px-3 py-2 flex items-center justify-between ${statusTextColor}`}
      >
        <div className="flex items-center gap-2 text-xs font-medium">
          {isError ? (
            <>
              <XCircle className="w-4 h-4" />
              Erro: {result.error}
            </>
          ) : (
            <>
              {isSuccess ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <XCircle className="w-4 h-4" />
              )}
              {result.status} {result.statusText}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 text-[10px] opacity-70">
            <Clock className="w-3 h-3" />
            {result.duration}ms
          </span>
          {result.size && (
            <span className="text-[10px] opacity-70">
              {result.size > 1024
                ? `${(result.size / 1024).toFixed(1)}KB`
                : `${result.size}B`}
            </span>
          )}
          <button
            type="button"
            className="text-xs opacity-60 hover:opacity-100"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Result Tabs */}
      {!isError && (
        <>
          <div className="flex border-t border-b bg-white/50 border-slate-200">
            <button
              type="button"
              className={`px-3 py-1 text-[11px] font-medium ${
                activeResultTab === 'body'
                  ? 'text-blue-600 border-b-2 border-blue-500'
                  : 'text-slate-500'
              }`}
              onClick={() => setActiveResultTab('body')}
            >
              Body
            </button>
            <button
              type="button"
              className={`px-3 py-1 text-[11px] font-medium ${
                activeResultTab === 'headers'
                  ? 'text-blue-600 border-b-2 border-blue-500'
                  : 'text-slate-500'
              }`}
              onClick={() => setActiveResultTab('headers')}
            >
              Headers
            </button>
            <button
              type="button"
              className={`px-3 py-1 text-[11px] font-medium ${
                activeResultTab === 'info'
                  ? 'text-blue-600 border-b-2 border-blue-500'
                  : 'text-slate-500'
              }`}
              onClick={() => setActiveResultTab('info')}
            >
              Info
            </button>
          </div>

          <div className="bg-white/30 max-h-48 overflow-auto">
            {activeResultTab === 'body' && (
              <div className="relative">
                <button
                  type="button"
                  className="absolute top-1 right-1 p-1 bg-white/80 rounded hover:bg-white text-slate-500"
                  onClick={() => onCopy(responseBody || '')}
                  title="Copiar"
                >
                  <Copy className="w-3 h-3" />
                </button>
                <pre className="p-2 text-[10px] font-mono text-slate-700 whitespace-pre-wrap break-all">
                  {responseBody || '(resposta vazia)'}
                </pre>
              </div>
            )}

            {activeResultTab === 'headers' && (
              <div className="p-2 space-y-0.5">
                {result.headers &&
                  Object.entries(result.headers).map(([key, value]) => (
                    <div key={key} className="text-[10px] font-mono">
                      <span className="text-blue-600">{key}</span>:{' '}
                      <span className="text-slate-600">{String(value)}</span>
                    </div>
                  ))}
              </div>
            )}

            {activeResultTab === 'info' && (
              <div className="p-2 space-y-1 text-[10px]">
                <div>
                  <span className="font-medium text-slate-600">Duração:</span>{' '}
                  {result.duration}ms
                </div>
                <div>
                  <span className="font-medium text-slate-600">Tamanho:</span>{' '}
                  {result.size
                    ? result.size > 1024
                      ? `${(result.size / 1024).toFixed(1)} KB`
                      : `${result.size} bytes`
                    : 'N/A'}
                </div>
                {result.truncated && (
                  <div className="text-amber-600">
                    ⚠ Resposta truncada (muito grande)
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
